import React from 'react';
import { useTheme } from '../../hooks/useTheme';
import { Link } from 'react-router-dom';
import { MapPin, Mail, Clock, CreditCard, Shield, Truck } from 'lucide-react';
import logoCompleto from '../../assets/Logo.png';

export const Footer: React.FC = () => {
  const { theme } = useTheme();

  return (
    <footer className={`${
      theme === 'dark'
        ? 'bg-gray-900 text-white'
        : 'bg-gray-50 text-gray-800'
    }`}>
      {/* Sección de beneficios */}
      <div className={`py-8 px-4 sm:px-6 lg:px-8 border-b ${
        theme === 'dark' ? 'border-gray-800' : 'border-gray-200'
      }`}>
        <div className="w-full">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="flex items-center space-x-3">
              <div className={`p-3 rounded-full ${
                theme === 'dark' ? 'bg-[#4CAF50]/20' : 'bg-gradient-to-r from-[#4CAF50]/10 to-[#2196F3]/10'
              }`}>
                <Truck className="w-6 h-6 text-[#2196F3] dark:text-[#4CAF50]" />
              </div>
              <div>
                <p className="font-semibold text-sm">Domicilio Gratis</p>
                <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>En pedidos +$50k</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className={`p-3 rounded-full ${
                theme === 'dark' ? 'bg-[#4CAF50]/20' : 'bg-gradient-to-r from-[#4CAF50]/10 to-[#2196F3]/10'
              }`}>
                <Shield className="w-6 h-6 text-[#2196F3] dark:text-[#4CAF50]" />
              </div>
              <div>
                <p className="font-semibold text-sm">Productos Originales</p>
                <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>100% Garantizados</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className={`p-3 rounded-full ${
                theme === 'dark' ? 'bg-[#4CAF50]/20' : 'bg-gradient-to-r from-[#4CAF50]/10 to-[#2196F3]/10'
              }`}>
                <CreditCard className="w-6 h-6 text-[#2196F3] dark:text-[#4CAF50]" />
              </div>
              <div>
                <p className="font-semibold text-sm">Pagos Seguros</p>
                <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Múltiples métodos</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className={`p-3 rounded-full ${
                theme === 'dark' ? 'bg-[#4CAF50]/20' : 'bg-gradient-to-r from-[#4CAF50]/10 to-[#2196F3]/10'
              }`}>
                <Clock className="w-6 h-6 text-[#2196F3] dark:text-[#4CAF50]" />
              </div>
              <div>
                <p className="font-semibold text-sm">Lun-Dom: 8AM - 9PM</p>
                <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Domicilios en horario</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Contenido principal del footer */}
      <div className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="w-full">
          <div className="grid lg:grid-cols-4 md:grid-cols-2 gap-8">
            
            {/* Logo y Descripción */}
            <div className="lg:col-span-1">
              <div className="mb-4">
                <img 
                  src={logoCompleto} 
                  alt="ConfiFarma Amiga" 
                  className={`h-16 w-auto object-contain ${theme === 'dark' ? 'brightness-110' : ''}`}
                />
              </div>
              
              <p className={`text-sm leading-relaxed mb-4 ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
              }`}>
                Tu farmacia amiga con más de 20 años de experiencia. 
                Ofrecemos productos de calidad, asesoría profesional y 
                servicio de domicilios para tu comodidad.
              </p>

              {/* Redes Sociales */}
              <div className="flex space-x-3">
                {[
                  { name: 'facebook', href: '#' },
                  { name: 'instagram', href: '#' },
                  { name: 'whatsapp', href: 'https://wa.me/573193650538' }
                ].map((social) => (
                  <a
                    key={social.name}
                    href={social.href}
                    target={social.name === 'whatsapp' ? '_blank' : undefined}
                    rel={social.name === 'whatsapp' ? 'noopener noreferrer' : undefined}
                    className={`w-9 h-9 rounded-lg flex items-center justify-center transition-all ${
                      social.name === 'facebook' ? 'bg-[#2196F3] hover:bg-[#1976D2]' :
                      social.name === 'instagram' ? 'bg-gradient-to-r from-[#4CAF50] to-[#2196F3] hover:opacity-90' :
                      'bg-[#25D366] hover:bg-[#128C7E]'
                    }`}
                  >
                    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                      {social.name === 'facebook' && (
                        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                      )}
                      {social.name === 'instagram' && (
                        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                      )}
                      {social.name === 'whatsapp' && (
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.567-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.50 3.488"/>
                      )}
                    </svg>
                  </a>
                ))}
              </div>
            </div>

            {/* Enlaces rápidos */}
            <div>
              <h4 className={`font-bold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                Enlaces Rápidos
              </h4>
              <ul className={`space-y-2 text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                <li><Link to="/" className="hover:text-[#2196F3] dark:hover:text-[#4CAF50] transition-colors">Inicio</Link></li>
                <li><Link to="/productos" className="hover:text-[#2196F3] dark:hover:text-[#4CAF50] transition-colors">Productos</Link></li>
                <li><Link to="/servicios" className="hover:text-[#2196F3] dark:hover:text-[#4CAF50] transition-colors">Servicios</Link></li>
                <li><Link to="/contacto" className="hover:text-[#2196F3] dark:hover:text-[#4CAF50] transition-colors">Contacto</Link></li>
                <li><Link to="/login" className="hover:text-[#2196F3] dark:hover:text-[#4CAF50] transition-colors">Mi Cuenta</Link></li>
              </ul>
            </div>

            {/* Categorías */}
            <div>
              <h4 className={`font-bold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                Categorías
              </h4>
              <ul className={`space-y-2 text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                <li><a href="#" className="hover:text-[#2196F3] dark:hover:text-[#4CAF50] transition-colors">Medicamentos</a></li>
                <li><a href="#" className="hover:text-[#2196F3] dark:hover:text-[#4CAF50] transition-colors">Cuidado Personal</a></li>
                <li><a href="#" className="hover:text-[#2196F3] dark:hover:text-[#4CAF50] transition-colors">Vitaminas y Suplementos</a></li>
                <li><a href="#" className="hover:text-[#2196F3] dark:hover:text-[#4CAF50] transition-colors">Bebés y Maternidad</a></li>
                <li><a href="#" className="hover:text-[#2196F3] dark:hover:text-[#4CAF50] transition-colors">Equipos Médicos</a></li>
              </ul>
            </div>

            {/* Información de Contacto */}
            <div>
              <h4 className={`font-bold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                Contáctanos
              </h4>
              <ul className={`space-y-3 text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
                <li className="flex items-start space-x-2">
                  <MapPin className="w-5 h-5 text-[#2196F3] dark:text-[#4CAF50] flex-shrink-0 mt-0.5" />
                  <span>Cra. 104 # 103 A - 24 <br />Apartadó, Antioquia, Colombia</span>
                </li>
                <li className="flex items-center space-x-2">
                  <svg className="w-5 h-5 text-[#2196F3] dark:text-[#4CAF50] flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.567-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.50 3.488"/>
                  </svg>
                  <a href="https://wa.me/573193650538" target="_blank" rel="noopener noreferrer" className="hover:text-[#2196F3] dark:hover:text-[#4CAF50] transition-colors">(+57) 319 3650538</a>
                </li>
                <li className="flex items-center space-x-2">
                  <Mail className="w-5 h-5 text-[#2196F3] dark:text-[#4CAF50] flex-shrink-0" />
                  <span>confifarma.amiga@gmail.com</span>
                </li>
                <li className="flex items-start space-x-2">
                  <Clock className="w-5 h-5 text-[#2196F3] dark:text-[#4CAF50] flex-shrink-0 mt-0.5" />
                  <span>Lun-Dom: 8:00 AM - 9:00 PM</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Copyright */}
      <div className={`py-4 px-4 sm:px-6 lg:px-8 border-t ${
        theme === 'dark' ? 'border-gray-800' : 'border-gray-200'
      }`}>
        <div className="w-full">
          <div className="flex flex-col md:flex-row justify-between items-center gap-2">
            <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
              &copy; 2026 ConfiFarma Amiga. Todos los derechos reservados.
            </p>
            <div className={`flex space-x-4 text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
              <a href="#" className="hover:text-[#2196F3] dark:hover:text-[#4CAF50] transition-colors">Términos y Condiciones</a>
              <a href="#" className="hover:text-[#2196F3] dark:hover:text-[#4CAF50] transition-colors">Política de Privacidad</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
